<template>
  <div class="search">
    <div class="container">
      <div class="search__wrapper">
        <div class="search__top">
          <div class="search__top-left">
              <router-link
                  :to="translations.i18nRoute({ name: 'market' })"
                  class="back"
              >
                {{ t('market.back') }}
              </router-link>
            <div class="search__top-buttons">
              <div class="search__top-button" @click="setNameOrdering">
                <p>{{ t('market.sortBy') }} <span>{{ t('market.name') }}</span></p>
                <span class="search__top-button-inner">
                <span
                    :class="orderBy === 'name' ? 'search__top-button-small' : 'search__top-button-active'"
                />
                <span />
                <span
                    :class="orderBy === 'name' ? 'search__top-button-active' : 'search__top-button-small'"
                />
              </span>
              </div>
              <div class="search__top-button" @click="setPriceOrdering">
                <p>{{ t('market.sortBy') }} <span>{{ t('market.price') }}</span></p>
                <span class="search__top-button-inner">
                <span
                    :class="orderBy === 'price' ? 'search__top-button-small' : 'search__top-button-active'"
                />
                <span />
                <span
                    :class="orderBy === 'price' ? 'search__top-button-active' : 'search__top-button-small'"
                />
              </span>
              </div>
            </div>
          </div>
          <form class="search__form" @submit.prevent="search">
            <input
                class="search__form-input"
                :placeholder="t('fields.headerSearch')"
                v-model="searchInput"
            />
          </form>
        </div>
        <div class="search__block" v-if="products.length > 0">
          <h2 class="search__title">{{ t('search.products') }} {{ t('search.byRequest') }} "{{ searchQuery }}"</h2>
          <div class="search__grid">
            <product-card
                v-for="product in products"
                :key="product.node.uuid"
                :product="product.node"
            />
          </div>
        </div>
        <div
            class="search__block-empty"
            v-if="!products.length && !categories.length"
        >
          <h2 class="search__title">{{ t('search.empty') }}</h2>
        </div>
        <div class="search__block" v-if="categories.length > 0">
          <h2 class="search__title">{{ t('search.categories') }} {{ t('search.byRequest') }} "{{ searchQuery }}"</h2>
          <div class="search__grid">
            <router-link
                :to="translations.i18nRoute({ name: 'market', query: { categoriesSlugs: category.node.slug } })"
                class="category"
                v-for="category in categories"
                :key="category.node.uuid"
            >
              <div class="category__inner">
                <img :src="category.node.image" :alt="category.node.name">
              </div>
              <p>{{ category.node.name }}</p>
            </router-link>
          </div>
        </div>
      </div>
    </div>
    <transition name="opacity" mode="out-in">
      <product-view
          v-if="activeViewProduct"
          :product="activeViewProduct"
      />
    </transition>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {Products} from "@/core/api/products.js";
import {Categories} from "@/core/api/categories.js";
import {useI18n} from "vue-i18n";
import ProductCard from "@/components/cards/product-card.vue";
import ProductView from "@/components/cards/product-view.vue";
import {useAppStore} from "@/stores/app.js";
import translations from "@/core/helpers/translations.js";


const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const appStore = useAppStore()


const searchQuery = computed(() =>
    String(route.params.searchQuery || '')
)

const orderBy = computed(() =>
    route.query.orderBy ? String(route.query.orderBy) : ''
)

const searchInput = ref(searchQuery.value)

watch(
    () => searchQuery.value,
    (val) => {
      searchInput.value = val
    }
)

const activeViewProduct = computed(() =>
    appStore.activeViewProduct
)

const search = () => {
  router.push({
    name: 'search',
    params: {
      searchQuery: searchInput.value,
      locale: localStorage.getItem('digitgiftcode-user-locale') || 'en-gb'
    }
  })
}

const toggleOrdering = (type) => {
  const current = orderBy.value
  const currentType = current.startsWith('-')
      ? current.slice(1)
      : current

  const newValue =
      currentType === type
          ? (current.startsWith('-') ? type : `-${type}`)
          : type

  router.replace({
    query: {
      ...route.query,
      orderBy: newValue || undefined
    }
  })
}

const setPriceOrdering = () => toggleOrdering('price')
const setNameOrdering = () => toggleOrdering('name')

const products = ref([])
const categories = ref([])

const getProductsByName = async () => {
  const res = await Products.getProducts(
      `first: 20` +
      `, name: "${searchQuery.value}"` +
      (orderBy.value ? `, orderBy: "${orderBy.value}"` : '')
  )

  products.value = res.data?.products?.edges || []
}

const getCategoriesByName = async () => {
  const res = await Categories.getCategoryByName(searchQuery.value)
  categories.value = res.data?.categories?.edges || []
}

watch(
    () => [searchQuery.value, orderBy.value],
    async () => {
      products.value = []
      categories.value = []

      await getProductsByName()
      await getCategoriesByName()
    },
    { immediate: true }
)
</script>


<style lang="scss" scoped>
.search {
  padding-top: 150px;

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 30px;
    padding-bottom: 30px;
    border-bottom: 1px solid #3B3B3B;

    &-left{
      display: flex;
      align-items: center;
      gap: 30px;
    }

    &-block {
      border-radius: 10px;
      background-color: #FFFFFF;
      padding: 10px 50px;

      border: 1px solid #3B3B3B;

      color: #3B3B3B;
      text-align: center;
      font-size: 20px;
      font-weight: 600;
      line-height: 25.801px;
    }

    &-buttons {
      display: flex;
      align-items: center;
      gap: 15px;
    }

    &-button {
      cursor: pointer;
      border-radius: 10px;
      background-color: #FFFFFF;
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 11px 15px;
      transition: 0.2s;

      border: 1px solid #3B3B3B;

      @include hover {
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
      }

      & p {
        color: #3B3B3B;
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;

        & span {
          font-weight: 700;
        }
      }

      &-inner {
        display: flex;
        align-items: start;
        flex-direction: column;
        justify-content: space-between;
        width: 40px;
        height: 23px;

        & span {
          height: 2px;
          background-color: #3B3B3B;
          border-radius: 0.5rem;
          width: 75%;
          transition: all 0.3s ease;
        }
      }

      &-small {
        width: 50% !important;
      }

      &-active {
        width: 100% !important;
      }
    }
  }

  &__form{
    background-image: url("@icons/search.svg");
    background-repeat: no-repeat;
    background-position: left 25px top 50%;
    background-color: #FFFFFF;
    border: #3b3b3b solid 1px;
    border-radius: 30px;
    padding: 13px 10px 13px 46px;

    width: 450px;

    & input{
      width: 100%;
    }
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 50px;
  }

  &__block {
    display: flex;
    flex-direction: column;
    gap: 25px;
  }

  &__title {
    color: #3b3b3b;
    font-size: 28px;
    font-weight: 700;
    line-height: 30px;
  }

  &__grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(225px, 225px));
    column-gap: 20px;
    row-gap: 30px;
    justify-content: space-between;
  }
}

.category {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  cursor: pointer;

  &__inner {
    background-color: #F5F5F5;
    border-radius: 50%;
    padding: 15px;
    width: 130px;
    height: 130px;
    display: grid;
    place-items: center;
    transition: 0.2s;

    & img {
      width: 100%;
    }
  }

  & p {
    color: #222;
    text-align: center;
    font-weight: 500;
    line-height: 20px;
  }

  @include hover {
    & div {
      filter: drop-shadow(0 0 0 rgba(0, 0, 0, 0.05)) drop-shadow(0 2px 5px rgba(0, 0, 0, 0.05)) drop-shadow(1px 10px 10px rgba(0, 0, 0, 0.04)) drop-shadow(2px 22px 13px rgba(0, 0, 0, 0.03)) drop-shadow(4px 40px 16px rgba(0, 0, 0, 0.01)) drop-shadow(6px 62px 17px rgba(0, 0, 0, 0.00));
    }
  }
}

.back{
  font-size: 20px;
  transition: 0.3s;

  @include hover {
    color: #979494;
  }
}

@media (max-width: 600px) {
  .search {
    &__grid {
      justify-content: center;
    }
  }
}
</style>